/**
 * Portfolio API Service (legacy-compatible)
 * Optimized and cleaned-up version.
 * Full service for interacting with the portfolio backend API.
 * Keeps compatibility with legacy code while being optimized.
 */

interface BackendAboutInfo {
  id: string;
  name_fr: string;
  name_en: string;
  title_fr: string;
  title_en: string;
  bio_fr: string;
  bio_en: string;
  about_title_fr: string;
  about_title_en: string;
  about_intro_fr: string;
  about_intro_en: string;
  about_career_fr: string;
  about_career_en: string;
  about_datascience_fr: string;
  about_datascience_en: string;
  about_specialization_fr: string;
  about_specialization_en: string;
  about_englishlevel_fr: string;
  about_englishlevel_en: string;
  about_rqth_fr: string;
  about_rqth_en: string;
  about_permis_fr: string;
  about_permis_en: string;
  linkedin_url: string;
  github_url: string;
  discord_url: string;
  slack_url: string;
  figma_url: string;
  trello_url: string;
  email: string;
  phone: string;
  location: string;
  profile_image_url: string;
  cv_url_fr: string;
  cv_url_en: string;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}



import type { 
  User, 
  ProjectBackend as Project, 
  Section, 
  AboutInfo, 
  LifeQuoteItem, 
  HobbyItem, 
  HobbyTheme,
  HobbyThemeItem,
  EducationItem, 
  Language, 
  ApiResponse,
  ApiError,                       
  AssetData, 
  AssetUploadMetadata,
  ProjectImage,
  ProjectFeature,
  LoginCredentials,
  SkillItem,
  SkillsByCategory,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateSectionRequest
} from '../../../types'; 

import apiErrorHandler from '../../../services/apiErrorHandler';
import type { ExtendedApiError } from '../../../services/apiErrorHandler';

import type {
  UpdateSectionRequest, 
  UpdateAboutRequest, 
  ReorderProjectsRequest, 
  ReorderSectionsRequest
} from '../../../types';

// ================================
// CONFIGURATION
// ================================

const getApiBaseUrl = (): string => {
  // In production, always use an environment variable
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Development only fallback (Node/Express backend is mounted under /api)
  if (import.meta.env.MODE === 'development') {
    return '/api';
  }
  
  throw new Error('VITE_API_URL must be configured in production environment');
};

const API_BASE_URL = getApiBaseUrl();

// ================================
// INTERFACES BACKEND
// ================================

interface BackendAuthResponse {
  success: boolean;
  data?: {
    user: User;
    token: string;
    expiresIn?: number;
  };
  message?: string;
  error?: string;
}

interface BackendDashboardStats {
  totalProjects: number;
  academicProjects: number;
  professionalProjects: number;
  projectsWithLinks: number;
  completionRate: number;
  totalSections: number;
  visibleSections: number;
  hiddenSections: number;
  visibilityRate: number;
  aboutConfigured: boolean;
  aboutCompletionScore: number;
  lastUpdated: string;
  overallHealth: number;
}

interface BackendProjectsFilter {
  academic?: boolean;
  professional?: boolean;
  search?: string;
  technology?: string;
  withLinks?: boolean;
  withFeatures?: boolean;
  visible?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: 'created_at' | 'updated_at' | 'title' | 'display_order';
  orderDirection?: 'asc' | 'desc';
}

interface BackendImageMetadata {
  filename: string;
  mimetype: string;
  size: number;
  width?: number;
  height?: number;
  url: string;
  alt_text?: string;
}

// ================================
// MAIN SERVICE
// ================================

class PortfolioApiService {
  // Authentication cache
  private _authCache: {
    isAuth: boolean;
    timestamp: number;
    token: string | null;
  } | null = null;

  private _userCache: {
    user: User;
    timestamp: number;
  } | null = null;

  private _verifyCache: {
    valid: boolean;
    user?: User;
    timestamp: number;
  } | null = null;

  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly USER_CACHE_DURATION = 30 * 1000; // 30 seconds for user calls
  private readonly VERIFY_CACHE_DURATION = 60 * 1000; // 1 minute for token verification
  private readonly TOKEN_STORAGE_KEY = 'auth_token';
  private readonly USER_STORAGE_KEY = 'portfolio_user';

  // ================================
  // TOKEN MANAGEMENT
  // ================================

  /**
 * Retrieve the auth token with multiple fallbacks
 */
getAuthToken(): string | null {
  try {
    // Search in all possible storage locations
    const sources = [
      localStorage.getItem(this.TOKEN_STORAGE_KEY),
      localStorage.getItem('portfolio_token'),
      localStorage.getItem('auth_token'),
      localStorage.getItem('token'),
      sessionStorage.getItem(this.TOKEN_STORAGE_KEY),
      sessionStorage.getItem('portfolio_token'),
      sessionStorage.getItem('auth_token')
    ];
    
    for (const token of sources) {
      if (token && token.length > 10) {
        // console.log('🔑 Token found - length:', token.length);
        return token;
      }
    }
    
    // No valid token found in storage
    return null;
  } catch (error) {
    // Failed to retrieve token
    return null;
  }
}

  /**
   * Save the authentication token
   */
  private setAuthToken(token: string): void {
    try {
      localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
    } catch (error) {
      // Failed to save token
    }
  }

  /**
 * Check if the token is expired with improved error handling
 */
isTokenExpired(): boolean {
  const token = this.getAuthToken();
  if (!token) {
    console.log('🔍 No token - considered not expired to avoid loops');
    return false; // Do not block if no token
  }
  
  try {
    // Check basic JWT structure
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('⚠️ Malformed token (not a JWT)');
      return false; // Could be a simple token, not necessarily expired
    }
    
    // Decode JWT payload
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    
    if (!exp) {
      console.log('🔍 Token without an expiration');
      return false; // No expiration defined
    }
    
    const now = Math.floor(Date.now() / 1000);
    const isExpired = exp < now;
    
    if (isExpired) {
      console.warn('⏰ Token expired at', new Date(exp * 1000));
      this.clearAuth(); // Auto-clean
    }
    
    return isExpired;
  } catch (error) {
    console.warn('⚠️ Unable to decode token:', error);
    return false; // Do not consider expired if we cannot decode
  }
}

  /**
 * Get authentication headers with debug
 */
getAuthHeaders(): Record<string, string> {
  const token = this.getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token && !this.isTokenExpired()) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

  // ================================
  // HTTP REQUESTS
  // ================================

/**
 * Effectue une requête HTTP avec gestion d'erreur centralisée.
 *
 * DÉDUPLICATION : si une requête identique (méthode + endpoint) est déjà
 * en cours, on retourne la promesse existante au lieu d'envoyer une seconde
 * requête. Cela évite les doublons lors de renders simultanés sans bloquer
 * les requêtes légitimes.
 *
 * NOTE : Le circuit breaker et le rate-limiter client ont été supprimés.
 * Ces mécanismes masquaient les boucles de requêtes sans traiter la cause
 * racine (dépendances instables dans les hooks React). La solution correcte
 * est de stabiliser les tableaux de dépendances useEffect/useCallback.
 */

// Déduplication : requêtes identiques en vol partagent la même promesse
private inFlightRequests = new Map<string, Promise<any>>();

private async makeRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Clé unique pour identifier une requête en vol (méthode + endpoint)
  const requestKey = `${options.method || 'GET'}:${endpoint}`;

  // Si une requête identique est déjà en cours, on la retourne directement
  if (this.inFlightRequests.has(requestKey)) {
    return this.inFlightRequests.get(requestKey)! as Promise<T>;
  }

  const url = `${API_BASE_URL}${endpoint}`;

  const config: RequestInit = {
    ...options,
    headers: {
      ...this.getAuthHeaders(),
      ...options.headers,
    },
  };

  // On enregistre la promesse AVANT l'await pour que les appels concurrents
  // puissent la trouver immédiatement dans inFlightRequests
  const execPromise = (async (): Promise<T> => {
    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Lecture du corps d'erreur pour extraire le message métier
        const responseText = await response.text();
        let responseData: any;
        try {
          responseData = JSON.parse(responseText);
        } catch {
          responseData = { message: responseText };
        }

        // Construction d'une erreur étendue avec le statut HTTP
        const apiError: ExtendedApiError = {
          name: 'ApiError',
          message:
            responseData?.error ||
            responseData?.message ||
            `Erreur HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
          code: responseData?.code || `HTTP_${response.status}`,
          details: {
            endpoint,
            method: (config.method as string) || 'GET',
            timestamp: new Date().toISOString(),
          },
        };

        // Gestion centralisée + redirection optionnelle vers les pages d'erreur
        try {
          apiErrorHandler.handleError(apiError, {
            autoRedirect: true,
            showNotification: false,
            context: `portfolioApi: ${endpoint}`,
          });
        } catch { /* apiErrorHandler ne doit jamais faire planter la requête */ }

        throw apiError;
      }

      return response.json() as Promise<T>;

    } catch (error: any) {
      // Wrapping des erreurs réseau en ExtendedApiError pour uniformité
      const extendedError: ExtendedApiError = (
        error && typeof error === 'object' &&
        'status' in error && 'code' in error && 'message' in error && 'name' in error
      )
        ? (error as ExtendedApiError)
        : {
            name: 'ApiError',
            message: error?.message || 'Erreur réseau ou inconnue',
            status: typeof error?.status === 'number' ? error.status : 500,
            code: error?.code || 'NETWORK_ERROR',
            details: {
              endpoint,
              method: (config.method as string) || 'GET',
              timestamp: new Date().toISOString(),
            },
          };

      try {
        apiErrorHandler.handleError(extendedError, {
          autoRedirect: true,
          showNotification: false,
          context: `portfolioApi: ${endpoint}`,
        });
      } catch { /* apiErrorHandler ne doit jamais faire planter la requête */ }

      throw extendedError;
    } finally {
      // Nettoyage de la déduplication — la requête n'est plus "en vol"
      this.inFlightRequests.delete(requestKey);
    }
  })();

  this.inFlightRequests.set(requestKey, execPromise);
  return execPromise;
}


  /**
   * File upload with FormData
   */
  private async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    const token = this.getAuthToken();
    const url = `${API_BASE_URL}${endpoint}`;

    console.log(`📤 Upload: ${endpoint}`);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && !this.isTokenExpired() && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Upload failed: ${response.statusText}`;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log(`✅ Upload Success: ${endpoint}`);
      return result;
      
    } catch (error: any) {
      console.error(`❌ Upload Error ${endpoint}:`, error.message);
      throw error;
    }
  }

  /**
   * Méthode de classe interne pour récupérer l'utilisateur actuel
   */
  private async _getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const now = Date.now();
      if (this._userCache && (now - this._userCache.timestamp) < this.USER_CACHE_DURATION) {
        return { success: true, data: this._userCache.user };
      }

      const token = this.getAuthToken();
      if (!token) throw new Error('Aucun token trouvé');
      
      const response = await this.makeRequest<{ success: boolean; data?: User; user?: User }>('/auth/me');
      const userData = response.data || response.user;
      
      if (userData) {
        this._userCache = { user: userData, timestamp: now };
        localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(userData));
        return { success: true, data: userData };
      }
      throw new Error('Données utilisateur absentes');
    } catch (error: any) {
      if (error.message.includes('401') || error.message.includes('403')) this.clearAuth();
      return { success: false, data: null as any, error: error.message };
    }
  }

  // ================================
  // AUTHENTICATION
  // ================================

  auth = {
    getCurrentUser: (): Promise<ApiResponse<User>> => this._getCurrentUser(),
  /**
 * User login with reinforced persistence
 */
login: async (credentials: LoginCredentials): Promise<ApiResponse<{
  user: User;
  token: string;
  expiresIn?: number;
}>> => {
  try {
    const response = await this.makeRequest<any>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    // Two-factor required: the backend returned a short-lived pre-auth token
    // instead of a full session. Stash it for the 2FA validation step.
    if (response.requiresTwoFactor && response.preAuthToken) {
      try { localStorage.setItem('pre_auth_token', response.preAuthToken); } catch {}
      return { success: false, data: null, error: '2FA_REQUIRED' };
    }

    if (response.success || response.token) {
      const token = response.data?.token || response.token;
      const user = response.data?.user || response.user;
      
      if (token && user) {
        // Save tokens in multiple keys
        try {
          localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
          localStorage.setItem('portfolio_token', token);
          localStorage.setItem('auth_token', token);
          localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(user));
          localStorage.setItem('portfolio_user', JSON.stringify(user));
        } catch (storageError) {
          console.error('❌ localStorage save error:', storageError);
        }
        
        // Reset authentication cache
        this._authCache = {
          isAuth: true,
          timestamp: Date.now(),
          token: token
        };
        
        return {
          success: true,
          data: {
            user,
            token,
            expiresIn: response.data?.expiresIn
          }
        };
      }
    }
    
  const errorMessage = response.error || response.message || 'Authentification échouée';
  console.error('❌ portfolioApi login failed:', errorMessage);
    
    return {
      success: false,
      data: null,
      error: errorMessage
    };
    
  } catch (error: any) {
  console.error('❌ Erreur connexion portfolioApi:', error.message);
    return {
      success: false,
      data: null,
  error: error.message || 'Erreur de connexion'
    };
  }
},

    /**
     * User logout
     */
    logout: async (): Promise<ApiResponse<boolean>> => {
      try {
        // Try server-side logout
        await this.makeRequest('/auth/logout', { method: 'POST' });
      } catch (error) {
        console.warn('⚠️ Server logout error (ignored):', error);
      } finally {
        // Always perform local cleanup
        this.clearAuth();
        console.log('🚪 Logged out');
      }
      
      return { success: true, data: true };
    },

    /**
     * Verify token validity
     */
    verifyToken: async (): Promise<ApiResponse<{ valid: boolean; user?: User }>> => {
      try {
        // Check cache first
        const now = Date.now();
        if (this._verifyCache && (now - this._verifyCache.timestamp) < this.VERIFY_CACHE_DURATION) {
          console.log('🎯 Token verification from cache');
          return {
            success: true,
            data: { 
              valid: this._verifyCache.valid, 
              user: this._verifyCache.user 
            }
          };
        }

        const response = await this.makeRequest<{ 
          success: boolean; 
          data?: { valid: boolean; user?: User };
          user?: User;
        }>('/auth/verify');
        
        // Support both response formats
        const valid = response.success && (response.data?.valid !== false);
        const user = response.data?.user || response.user;
        
        // Update cache
        this._verifyCache = {
          valid,
          user,
          timestamp: now
        };
        
        return {
          success: response.success,
          data: { valid, user }
        };
      } catch (error: any) {
  console.warn('⚠️ Token verification failed:', error.message);
        return {
          success: false,
          data: { valid: false },
          error: error.message
        };
      }
    },

    /**
     * User registration (if implemented)
     */
    register: async (userData: {
      email: string;
      password: string;
      name: string;
    }): Promise<ApiResponse<{ user: User; token: string }>> => {
      try {
        const response = await this.makeRequest<BackendAuthResponse>('/auth/register', {
          method: 'POST',
          body: JSON.stringify(userData),
        });
        
        if (response.success && response.data?.token) {
          this.setAuthToken(response.data.token);
          localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(response.data.user));
          
          return {
            success: true,
            data: response.data
          };
        }
        
        return {
          success: false,
          data: null,
          error: response.error || 'Inscription échouée'
        };
      } catch (error: any) {
        return {
          success: false,
          data: null,
          error: error.message
        };
      }
    },

  /**
   * Check if the user is authenticated
   */
  checkAuth: async (): Promise<ApiResponse<{ isAuthenticated: boolean; user?: User }>> => {
    try {
      const token = this.getAuthToken();
      
      if (!token) {
        return {
          success: true,
          data: { isAuthenticated: false }
        };
      }
      
      // Try to fetch current user
      const userResponse = await this._getCurrentUser();
      
      if (userResponse.success && userResponse.data) {
        return {
          success: true,
          data: {
            isAuthenticated: true,
            user: userResponse.data
          }
        };
      } else {
        return {
          success: true,
          data: { isAuthenticated: false }
        };
      }
    } catch (error: any) {
      console.error('❌ checkAuth error:', error);
      return {
        success: true,
        data: { isAuthenticated: false }
      };
    }
  },

    /**
     * Check if the current user has administrative permissions
     */
    checkAdminPermissions: async (): Promise<{ isAdmin: boolean }> => {
      try {
        const response = await this._getCurrentUser();
        const user = response.data;
        return { 
          isAdmin: !!(user && (user.isAdmin || user.is_admin || user.role === 'admin')) 
        };
      } catch (error) {
        return { isAdmin: false };
      }
    },

    /**
     * Refresh the token
     */
    refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
      try {
        console.log('🔄 Refreshing token...');
        const currentToken = this.getAuthToken();
        
        if (!currentToken) {
          throw new Error('No token to refresh');
        }
        
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: { token: string } 
        }>('/auth/refresh', {
          method: 'POST'
        });
        
        if (response.success && response.data?.token) {
          this.setAuthToken(response.data.token);
          console.log('✅ Token refreshed successfully');
          
          return {
            success: true,
            data: { token: response.data.token }
          };
        } else {
          throw new Error('Invalid refresh response');
        }
        
      } catch (error: any) {
        console.error('❌ refreshToken error:', error);
        this.clearAuth(); // Clean up on failure
        
        return {
          success: false,
          data: null,
          error: error.message
        };
      }
    },

  /**
   * Vérifier si un token existe
   */
  hasToken: (): boolean => {
    const token = this.getAuthToken();
    return !!token && token.length > 0;
  },

  /**
   * Récupérer le token actuel
   */
  getToken: (): string | null => {
    return this.getAuthToken();
  },

    /**
     * Check admin permissions via backend
     */
    checkAdmin: async (): Promise<boolean> => {
      const result = await this.checkAdminPermissions();
      return !!result.isAdmin;
    }
  };

  /**
   * Explicit admin permission check (backend)
   */
  async checkAdminPermissions(): Promise<{ success: boolean; isAdmin: boolean }> {
    try {
      const response = await this.makeRequest<{
        success: boolean;
        data?: { isAdmin: boolean };
      }>("/auth/admin-permissions");
      return { success: response.success !== false, isAdmin: !!response.data?.isAdmin };
    } catch (error) {
      return { success: false, isAdmin: false };
    }
  }

// ================================
// PROJECTS
// ================================
projects = {
    getAll: async (filters?: BackendProjectsFilter): Promise<ApiResponse<Project[]>> => {
      const endpoint = filters
        ? `/projects?${new URLSearchParams(
            Object.entries(filters)
              .filter(([,v]) => v != null)
              .reduce((acc, [k,v]) => (acc[k]=String(v), acc), {} as Record<string,string>)
          )}`
        : '/projects';
      const response = await this.makeRequest<ApiResponse<Project[]>>(endpoint);
      return {
        success: response.success,
        data: response.data ?? [],
        count: response.count,
        message: response.message
      };
    },

    getById: async (id: string): Promise<ApiResponse<Project | null>> => {
      const response = await this.makeRequest<ApiResponse<Project>>(`/projects/${id}`);
      return {
        success: response.success,
        data: response.success ? response.data : null,
        error: response.error
      };
    },

create: async (data: CreateProjectRequest): Promise<ApiResponse<Project>> => {
    try {
      const response = await this.makeRequest<ApiResponse<Project>>('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return {
        success: response.success,
        data: response.data ?? null,
        message: response.message,
        error: response.error
      };
    } catch (error: any) {
      return { success: false, data: null, error: error.message };
    }
  },

  update: async (id: string, data: UpdateProjectRequest): Promise<ApiResponse<Project>> => {
    try {
      const response = await this.makeRequest<ApiResponse<Project>>(`/projects/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return {
        success: response.success,
        data: response.data ?? null,
        message: response.message,
        error: response.error
      };
    } catch (error: any) {
      return { success: false, data: null, error: error.message };
    }
  },

  delete: async (id: string): Promise<ApiResponse<boolean>> => {
    try {
      await this.makeRequest(`/projects/${id}`, { method: 'DELETE' });
      return { success: true, data: true };
    } catch (error: any) {
      return { success: false, data: false, error: error.message };
    }
  },

  reorder: async (orders: ReorderProjectsRequest): Promise<ApiResponse<boolean>> => {
    try {
      await this.makeRequest('/projects/reorder', {
        method: 'POST',
        body: JSON.stringify(orders),
      });
      return { success: true, data: true };
    } catch (error: any) {
      return { success: false, data: false, error: error.message };
    }
  },

    toggleVisibility: async (id: string): Promise<ApiResponse<Project | null>> => {
      try {
        const response = await this.makeRequest<{
          success: boolean;
          data?: Project;
          message?: string;
          error?: string;
        }>(`/projects/${id}/toggle-visibility`, { method: 'PATCH' });
        if (response.success && response.data) {
          return { success: true, data: response.data, message: response.message };
        }
        return { success: false, data: null, error: response.error };
      } catch (e: any) {
        return { success: false, data: null, error: e.message };
      }
    }
  };

  // ================================
  // PROJECT FEATURES
  // ================================

  projectFeatures = {
  /**
   * Get a project's features
   */
  getProjectFeatures: async (projectId: string): Promise<ApiResponse<ProjectFeature[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: ProjectFeature[] 
    }>(`/features/${projectId}/features`);
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Add a feature to a project
   */
  addFeature: async (projectId: string, featureName: string, featureDescription: string): Promise<ApiResponse<ProjectFeature>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: ProjectFeature 
    }>(`/features/${projectId}/features`, {
          method: 'POST',
          body: JSON.stringify({
            name: featureName,
            description: featureDescription
          }),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update a feature
   */
  updateFeature: async (featureId: string, featureName: string, featureDescription: string): Promise<ApiResponse<ProjectFeature>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: ProjectFeature 
    }>(`/features/${featureId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: featureName,
            description: featureDescription
          }),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Delete a feature
   */
  deleteFeature: async (featureId: string): Promise<ApiResponse<boolean>> => {
      try {
    await this.makeRequest(`/features/${featureId}`, { 
          method: 'DELETE' 
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // PROJECT IMAGES
  // ================================

  projectImages = {
  /**
   * Get project images
   */
    getProjectImages: async (projectId: string): Promise<ApiResponse<ProjectImage[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: ProjectImage[] 
        }>(`/projects/${projectId}/images`);
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Delete a project image
   */
    delete: async (imageId: string): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest(`/project-images/${imageId}`, { 
          method: 'DELETE' 
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    },

  /**
   * Add an image to a project
   */
    add: async (projectId: string, imageData: Omit<ProjectImage, 'id' | 'project_id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<ProjectImage>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: ProjectImage 
        }>(`/projects/${projectId}/images`, {
          method: 'POST',
          body: JSON.stringify(imageData),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update a project image
   */
    update: async (imageId: string, data: Partial<ProjectImage>): Promise<ApiResponse<ProjectImage>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: ProjectImage 
        }>(`/project-images/${imageId}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // SETTINGS (feature flags & config)
  // ================================
  // NOTE: settings API defined elsewhere in this service file; duplicate removed.

  // ================================
  // SECTIONS
  // ================================

  sections = {
  /**
   * Get all sections
   */
    getAll: async (): Promise<ApiResponse<Section[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: Section[]; 
          count?: number;
        }>('/sections');
        
        return {
          success: response.success !== false,
          data: response.data || [],
          count: response.count || response.data?.length || 0
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Get a section by ID
   */
    getById: async (id: string): Promise<ApiResponse<Section>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: Section 
        }>(`/sections/${id}`);
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Get a section by name
   */
    getByName: async (name: string): Promise<ApiResponse<Section | null>> => {
      try {
        const response = await this.makeRequest<Section>(`/sections/by-name/${encodeURIComponent(name)}`);
        return { success: true, data: response || null };
      } catch (error: any) {
        if (error.message?.includes('404') || error.message?.includes('Not found')) {
          return { success: true, data: null };
        }
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Create a new section
   */
    create: async (data: CreateSectionRequest): Promise<ApiResponse<Section>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: Section 
        }>('/sections', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update a section
   */
    update: async (id: string, data: UpdateSectionRequest): Promise<ApiResponse<Section>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: Section 
        }>(`/sections/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Delete a section
   */
    delete: async (id: string): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest(`/sections/${id}`, { 
          method: 'DELETE' 
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    },

  /**
   * Reorder sections
   */
    reorder: async (orders: ReorderSectionsRequest): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest('/sections/reorder', {
          method: 'POST',
          body: JSON.stringify(orders),
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    },

  /**
   * Toggle section visibility
   */
    toggleVisibility: async (id: string): Promise<ApiResponse<Section>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: Section 
        }>(`/sections/${id}/visibility`, {
          method: 'PATCH',
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // ABOUT
  // ================================

  about = {
  /**
   * Get About information
   */
    get: async (): Promise<ApiResponse<AboutInfo>> => {
      try {
        // Cache court pour limiter les rafales
        const { apiService } = await import('../services/apiService');
        const response = await apiService.get<AboutInfo>(
          '/about',
          true,
          { useCache: true, cacheTtlMs: 120_000, dedupe: true, timeout: 10000 }
        );
        
        return {
          success: response.success !== false,
          data: response.data as any
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update About information
   */
    update: async (data: UpdateAboutRequest): Promise<ApiResponse<AboutInfo>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: AboutInfo 
        }>('/about', {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // SKILLS
  // ================================

  skills = {
  /**
   * Get all skills
   */
    getAll: async (): Promise<ApiResponse<SkillItem[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: SkillItem[]; 
          count?: number; 
          message?: string 
        }>('/skills');
        
        return {
          success: response.success !== false,
          data: response.data || [],
          count: response.count || response.data?.length || 0,
          message: response.message || `${response.data?.length || 0} compétences récupérées`
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message || 'Erreur lors de la récupération des compétences' 
        };
      }
    },

  /**
   * Get skills by category
   */
    getByCategory: async (lang: Language = 'fr'): Promise<ApiResponse<SkillsByCategory>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: SkillsByCategory; 
          message?: string 
        }>(`/skills/by-category?lang=${lang}`);
        
        return {
          success: response.success !== false,
          data: response.data || {},
          message: response.message || `Compétences groupées par catégorie (${lang})`
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: {}, 
          error: error.message || 'Erreur lors de la récupération des compétences par catégorie' 
        };
      }
    },

  /**
   * Get favorite skills
   */
    getFavorites: async (): Promise<ApiResponse<SkillItem[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: SkillItem[]; 
          count?: number; 
          message?: string 
        }>('/skills/favorites');
        
        return {
          success: response.success !== false,
          data: response.data || [],
          count: response.count || response.data?.length || 0,
          message: response.message || `${response.data?.length || 0} compétences favorites`
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message || 'Erreur lors de la récupération des compétences favorites' 
        };
      }
    },

  /**
   * Get a skill by ID
   */
    getById: async (id: string): Promise<ApiResponse<SkillItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: SkillItem; 
          message?: string 
        }>(`/skills/${id}`);
        
        return {
          success: response.success !== false,
          data: response.data,
          message: response.message || 'Compétence récupérée avec succès'
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message || 'Erreur lors de la récupération de la compétence' 
        };
      }
    },

  /**
   * Create a new skill
   */
    create: async (data: Omit<SkillItem, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<SkillItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: SkillItem; 
          message?: string 
        }>('/skills', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data,
          message: response.message || 'Compétence créée avec succès'
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message || 'Erreur lors de la création de la compétence' 
        };
      }
    },

  /**
   * Update a skill
   */
    update: async (id: string, data: Partial<SkillItem>): Promise<ApiResponse<SkillItem>> => {
      try {
        // Nettoyer les données pour ne garder que les champs compatibles backend
        const cleanData: any = {};
        
        // Champs obligatoires backend (s'ils sont fournis)
        if (data.name_fr !== undefined) cleanData.name_fr = data.name_fr;
        if (data.name_en !== undefined) cleanData.name_en = data.name_en;
        if (data.category_fr !== undefined) cleanData.category_fr = data.category_fr;
        if (data.category_en !== undefined) cleanData.category_en = data.category_en;
        if (data.icon_type !== undefined) cleanData.icon_type = data.icon_type;
        
        // Champs optionnels backend
        if ((data as any).image_url !== undefined) cleanData.image_url = (data as any).image_url;
        if (data.color !== undefined) cleanData.color = data.color;
        if (data.level !== undefined) cleanData.level = data.level;
        if (data.proficiency_level !== undefined) cleanData.proficiency_level = data.proficiency_level;
        if (data.is_favorite !== undefined) cleanData.is_favorite = data.is_favorite;
        if (data.is_visible !== undefined) cleanData.is_visible = data.is_visible;
        if (data.order_index !== undefined) cleanData.order_index = data.order_index;
        if (data.years_experience !== undefined) cleanData.years_experience = data.years_experience;
        if (data.description_fr !== undefined) cleanData.description_fr = data.description_fr;
        if (data.description_en !== undefined) cleanData.description_en = data.description_en;
        if (data.certification_url !== undefined) cleanData.certification_url = data.certification_url;
        
        // NE PAS envoyer les champs legacy qui causent des 500 :
        // - category (alias), name, description, proficiency, icon, icon_value, etc.
        
        console.log('🔧 Skill update data (cleaned):', cleanData);
        
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: SkillItem; 
          message?: string 
        }>(`/skills/${id}`, {
          method: 'PUT',
          body: JSON.stringify(cleanData),
        });
        
        return {
          success: response.success !== false,
          data: response.data,
          message: response.message || 'Compétence mise à jour avec succès'
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message || 'Erreur lors de la mise à jour de la compétence' 
        };
      }
    },

  /**
   * Delete a skill
   */
    delete: async (id: string): Promise<ApiResponse<boolean>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          message?: string 
        }>(`/skills/${id}`, { 
          method: 'DELETE' 
        });
        
        return {
          success: response.success !== false,
          data: true,
          message: response.message || 'Compétence supprimée avec succès'
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message || 'Erreur lors de la suppression de la compétence' 
        };
      }
    },

  /**
   * Toggle favorite status of a skill
   */
    toggleFavorite: async (id: string): Promise<ApiResponse<SkillItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: SkillItem; 
          message?: string 
        }>(`/skills/${id}/favorite`, {
          method: 'PATCH',
        });
        
        return {
          success: response.success !== false,
          data: response.data,
          message: response.message || 'Statut favori mis à jour'
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message || 'Erreur lors de la mise à jour du statut favori' 
        };
      }
    },

  /**
   * Toggle visibility of a skill
   */
    toggleVisibility: async (id: string): Promise<ApiResponse<SkillItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: SkillItem; 
          message?: string 
        }>(`/skills/${id}/visibility`, {
          method: 'PATCH',
        });
        
        return {
          success: response.success !== false,
          data: response.data,
          message: response.message || 'Visibilité mise à jour'
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message || 'Erreur lors de la mise à jour de la visibilité' 
        };
      }
    },

  /**
   * Reorder skills
   */
    reorder: async (orders: { id: string; order_index: number }[]): Promise<ApiResponse<boolean>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          message?: string 
        }>('/skills/reorder', {
          method: 'POST',
          body: JSON.stringify({ skillOrders: orders }),
        });
        
        return {
          success: response.success !== false,
          data: true,
          message: response.message || 'Ordre des compétences mis à jour'
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message || 'Erreur lors de la réorganisation des compétences' 
        };
      }
    },

  /**
   * Get available icons
   */
    getAvailableIcons: async (): Promise<ApiResponse<{ name: string; value: string; category: string }[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: { name: string; value: string; category: string }[]; 
          message?: string 
        }>('/skills/icons');
        
        return {
          success: response.success !== false,
          data: response.data || [],
          message: response.message || `${response.data?.length || 0} icônes disponibles`
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message || 'Erreur lors de la récupération des icônes' 
        };
      }
    }
  };

  // ================================
  // EDUCATION
  // ================================

  education = {
  /**
   * Get all education items
   */
    getAll: async (): Promise<ApiResponse<EducationItem[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: EducationItem[] 
        }>('/education');
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Get an education item by ID
   */
    getById: async (id: string): Promise<ApiResponse<EducationItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: EducationItem 
        }>(`/education/${id}`);
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Create a new education item
   */
    create: async (data: Omit<EducationItem, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<EducationItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: EducationItem 
        }>('/education', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update an education item
   */
    update: async (id: string, data: Partial<EducationItem>): Promise<ApiResponse<EducationItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: EducationItem 
        }>(`/education/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Delete an education item
   */
    delete: async (id: string): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest(`/education/${id}`, { 
          method: 'DELETE' 
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // PROFESSIONAL EXPERIENCES
  // ================================

  experiences = {
  /**
   * Get all experiences
   */
    getAll: async (): Promise<ApiResponse<any[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: any[] 
        }>('/experiences');
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Get an experience by ID
   */
    getById: async (id: string): Promise<ApiResponse<any>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: any 
        }>(`/experiences/${id}`);
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Create a new experience
   */
    create: async (data: any): Promise<ApiResponse<any>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: any 
        }>('/experiences', {
          method: 'POST',
          body: JSON.stringify(data)
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update an experience
   */
    update: async (id: string, data: any): Promise<ApiResponse<any>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: any 
        }>(`/experiences/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data)
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Delete an experience
   */
    delete: async (id: string): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest(`/experiences/${id}`, { 
          method: 'DELETE' 
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // HOBBIES
  // ================================

  hobbies = {
  /**
   * Get all hobbies
   */
    getAll: async (): Promise<ApiResponse<HobbyItem[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyItem[] 
        }>('/hobbies');
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Get a hobby by ID
   */
    getById: async (id: string): Promise<ApiResponse<HobbyItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyItem 
        }>(`/hobbies/${id}`);
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Create a new hobby
   */
    create: async (data: Omit<HobbyItem, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<HobbyItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyItem 
        }>('/hobbies', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update a hobby
   */
    update: async (id: string, data: Partial<HobbyItem>): Promise<ApiResponse<HobbyItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyItem 
        }>(`/hobbies/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Delete a hobby
   */
    delete: async (id: string): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest(`/hobbies/${id}`, { 
          method: 'DELETE' 
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // HOBBY THEMES
  // ================================

  hobbyThemes = {
  /**
   * Get all hobby themes
   */
    getAll: async (): Promise<ApiResponse<HobbyTheme[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyTheme[] 
        }>('/hobby-themes');
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Get all themes with their items
   */
    getAllWithItems: async (): Promise<ApiResponse<HobbyTheme[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyTheme[] 
        }>('/hobby-themes/with-items');
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Get a theme by ID
   */
    getById: async (id: string): Promise<ApiResponse<HobbyTheme>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyTheme 
        }>(`/hobby-themes/${id}`);
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Create a new theme
   */
    create: async (data: Omit<HobbyTheme, 'id' | 'created_at' | 'updated_at' | 'items'>): Promise<ApiResponse<HobbyTheme>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyTheme 
        }>('/hobby-themes', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update a theme
   */
    update: async (id: string, data: Partial<HobbyTheme>): Promise<ApiResponse<HobbyTheme>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyTheme 
        }>(`/hobby-themes/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Delete a theme
   */
    delete: async (id: string): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest(`/hobby-themes/${id}`, { 
          method: 'DELETE' 
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    },

  /**
   * Reorder themes
   */
    reorder: async (orderData: Array<{ id: string; order_index: number }>): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest('/hobby-themes/reorder', {
          method: 'PUT',
          body: JSON.stringify({ orderData }),
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // SETTINGS (FEATURE FLAGS)
  // ================================
  settings = {
    getAll: async (publicOnly = true): Promise<ApiResponse<{ key: string; value: any; is_public: boolean; description?: string }[]>> => {
      try {
        const response = await this.makeRequest<{ success: boolean; data: any[] }>(`/settings?public=${publicOnly ? 'true' : 'false'}`);
        return { success: response.success !== false, data: response.data || [] };
      } catch (error: any) {
        return { success: false, data: [], error: error.message };
      }
    },
    get: async (key: string): Promise<ApiResponse<{ key: string; value: any; is_public: boolean; description?: string }>> => {
      try {
        const response = await this.makeRequest<{ success: boolean; data: any }>(`/settings/${encodeURIComponent(key)}`);
        return { success: response.success !== false, data: response.data };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },
    set: async (key: string, value: any, is_public = true, description = ''): Promise<ApiResponse<boolean>> => {
      try {
        const response = await this.makeRequest<{ success: boolean; data: any }>(`/settings`, {
          method: 'POST',
          body: JSON.stringify({ key, value, is_public, description }),
        });
        return { success: response.success !== false, data: true };
      } catch (error: any) {
        return { success: false, data: false, error: error.message };
      }
    }
  };

  // ================================
  // HOBBY ITEMS (within themes)
  // ================================

  hobbyItems = {
  /**
   * Get all items
   */
    getAll: async (): Promise<ApiResponse<HobbyThemeItem[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyThemeItem[] 
        }>('/hobby-items');
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Get items for a theme
   */
    getByThemeId: async (themeId: string): Promise<ApiResponse<HobbyThemeItem[]>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyThemeItem[] 
        }>(`/hobby-items/theme/${themeId}`);
        
        return {
          success: response.success !== false,
          data: response.data || []
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: [], 
          error: error.message 
        };
      }
    },

  /**
   * Get an item by ID
   */
    getById: async (id: string): Promise<ApiResponse<HobbyThemeItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyThemeItem 
        }>(`/hobby-items/${id}`);
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Create a new item
   */
    create: async (data: Omit<HobbyThemeItem, 'id' | 'created_at' | 'updated_at' | 'theme'>): Promise<ApiResponse<HobbyThemeItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyThemeItem 
        }>('/hobby-items', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Update an item
   */
    update: async (id: string, data: Partial<HobbyThemeItem>): Promise<ApiResponse<HobbyThemeItem>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: HobbyThemeItem 
        }>(`/hobby-items/${id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    },

  /**
   * Delete an item
   */
    delete: async (id: string): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest(`/hobby-items/${id}`, { 
          method: 'DELETE' 
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    },

  /**
   * Reorder items
   */
    reorder: async (orderData: Array<{ id: string; order_index: number }>): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest('/hobby-items/reorder', {
          method: 'PUT',
          body: JSON.stringify({ orderData }),
        });
        return { success: true, data: true };
      } catch (error: any) {
        return { 
          success: false, 
          data: false, 
          error: error.message 
        };
      }
    }
  };

  /**
 * Perform a request with automatic retry when rate-limited
 */
private async makeRequestWithRetry<T>(
  endpoint: string, 
  options: RequestInit = {},
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await this.makeRequest<T>(endpoint, options);
    } catch (error: any) {
      lastError = error;
      
  // If it's a rate limit and we still have attempts
      if (error.message.includes('RATE_LIMIT_EXCEEDED') && attempt < maxRetries) {
        const waitTime = error.message.includes(':') 
          ? parseInt(error.message.split(':')[1]) 
          : baseDelay * Math.pow(2, attempt); // Exponential backoff
        
  console.log(`⏰ Rate limit - Tentative ${attempt + 1}/${maxRetries + 1} - Attente ${waitTime}ms`);
        
  // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }
      
  // For other errors, do not retry
      throw error;
    }
  }
  
  throw lastError!;
}

// Use the new retry method for critical operations
lifeQuotes = {
  /**
   * Get all quotes with retry
   */
  getAll: async (): Promise<ApiResponse<LifeQuoteItem[]>> => {
    try {
      console.log('📡 Récupération de toutes les citations...');
      const response = await this.makeRequestWithRetry<{ 
        success: boolean; 
        data: LifeQuoteItem[] 
      }>('/life-quotes');
      
      console.log('📡 Réponse API life-quotes:', response);
      
      return {
        success: response.success !== false,
        data: response.data || []
      };
    } catch (error: any) {
      console.error('❌ Erreur getAll life-quotes:', error);
      return { 
        success: false, 
        data: [], 
        error: error.message 
      };
    }
  },

  /**
   * Create a new quote with retry
   */
  create: async (data: Omit<LifeQuoteItem, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<LifeQuoteItem>> => {
    try {
      console.log('📡 Création d\'une nouvelle citation:', data);
      const response = await this.makeRequestWithRetry<{ 
        success: boolean; 
        data: LifeQuoteItem 
      }>('/life-quotes', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      return {
        success: response.success !== false,
        data: response.data || null
      };
    } catch (error: any) {
      console.error('❌ Erreur create life-quote:', error);
      return { 
        success: false, 
        data: null, 
        error: error.message 
      };
    }
  },

  
  /**
   * Update a quote
   */
  update: async (id: string, data: Partial<LifeQuoteItem>): Promise<ApiResponse<LifeQuoteItem>> => {
    try {
      console.log('📡 Mise à jour citation:', id, data);
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: LifeQuoteItem 
      }>(`/life-quotes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      
      // ✅ CORRECTION - Assurer le retour d'un LifeQuoteItem valide
      return {
        success: response.success !== false,
        data: response.data || null
      };
    } catch (error: any) {
      console.error('❌ Erreur update life-quote:', error);
      return { 
        success: false, 
        data: null, 
        error: error.message 
      };
    }
  },

  /**
   * Delete a quote
   */
  delete: async (id: string): Promise<ApiResponse<boolean>> => {
    try {
      console.log('📡 Suppression citation:', id);
      await this.makeRequest(`/life-quotes/${id}`, { 
        method: 'DELETE' 
      });
      return { success: true, data: true };
    } catch (error: any) {
      console.error('❌ Erreur delete life-quote:', error);
      return { 
        success: false, 
        data: false, 
        error: error.message 
      };
    }
  },

  
/**
  * Get visible quotes (public)
  */
  getVisible: async (): Promise<ApiResponse<LifeQuoteItem[]>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: LifeQuoteItem[] 
      }>('/life-quotes?visible=true');
      
      return {
        success: response.success !== false,
        data: response.data || []
      };
    } catch (error: any) {
      console.error('❌ Erreur getVisible life-quotes:', error);
      return { 
        success: false, 
        data: [], 
        error: error.message 
      };
    }
  },

  /**
   * Get active quotes
   */
  getActive: async (): Promise<ApiResponse<LifeQuoteItem[]>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: LifeQuoteItem[] 
      }>('/life-quotes/active');
      
      return {
        success: response.success !== false,
        data: response.data || []
      };
    } catch (error: any) {
      console.error('❌ Erreur getActive life-quotes:', error);
      return { 
        success: false, 
        data: [], 
        error: error.message 
      };
    }
  },

  /**
   * Get the current active quote (single)
   */
  getCurrent: async (): Promise<ApiResponse<LifeQuoteItem | null>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: LifeQuoteItem | null 
      }>('/life-quotes/current');
      
      return {
        success: response.success !== false,
        data: response.data || null
      };
    } catch (error: any) {
      console.error('❌ Erreur getCurrent life-quotes:', error);
      return { 
        success: false, 
        data: null, 
        error: error.message 
      };
    }
  },

  /**
   * Toggle visibility
   */
  toggleVisibility: async (id: string): Promise<ApiResponse<LifeQuoteItem>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: LifeQuoteItem 
      }>(`/life-quotes/${id}/visibility`, {
        method: 'PATCH'
      });
      
      return {
        success: response.success !== false,
        data: response.data
      };
    } catch (error: any) {
      console.error('❌ Erreur toggleVisibility life-quotes:', error);
      return { 
        success: false, 
        data: null, 
        error: error.message 
      };
    }
  },

  /**
   * Toggle active status
   */
  toggleActive: async (id: string): Promise<ApiResponse<LifeQuoteItem>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: LifeQuoteItem 
      }>(`/life-quotes/${id}/toggle-active`, {
        method: 'PATCH'
      });
      
      return {
        success: response.success !== false,
        data: response.data
      };
    } catch (error: any) {
      console.error('❌ Erreur toggleActive life-quotes:', error);
      return { 
        success: false, 
        data: null, 
        error: error.message 
      };
    }
  },

  /**
   * Set as the single active quote
   */
  setAsActive: async (id: string): Promise<ApiResponse<LifeQuoteItem>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: LifeQuoteItem 
      }>(`/life-quotes/${id}/set-active`, {
        method: 'PATCH'
      });
      
      return {
        success: response.success !== false,
        data: response.data
      };
    } catch (error: any) {
      console.error('❌ Erreur setAsActive life-quotes:', error);
      return { 
        success: false, 
        data: null, 
        error: error.message 
      };
    }
  },

  /**
   * Reorder quotes
   */
  reorder: async (quoteOrders: Array<{ id: string; order_index: number }>): Promise<ApiResponse<boolean>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean 
      }>('/life-quotes/reorder', {
        method: 'POST',
        body: JSON.stringify({ quoteOrders })
      });
      
      return {
        success: response.success !== false,
        data: true
      };
    } catch (error: any) {
      console.error('❌ Erreur reorder life-quotes:', error);
      return { 
        success: false, 
        data: false, 
        error: error.message 
      };
    }
  },

  /**
   * Get statistics
   */
  getStatistics: async (): Promise<ApiResponse<{ total: number; visible: number; active: number; hidden: number }>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: { total: number; visible: number; active: number; hidden: number } 
      }>('/life-quotes/statistics');
      
      return {
        success: response.success !== false,
        data: response.data || { total: 0, visible: 0, active: 0, hidden: 0 }
      };
    } catch (error: any) {
      console.error('❌ Erreur getStatistics life-quotes:', error);
      return { 
        success: false, 
        data: { total: 0, visible: 0, active: 0, hidden: 0 }, 
        error: error.message 
      };
    }
  },

  /**
   * Search quotes
   */
  search: async (params: { query?: string; author?: string; language?: 'fr' | 'en' }): Promise<ApiResponse<LifeQuoteItem[]>> => {
    try {
      const searchParams = new URLSearchParams();
      if (params.query) searchParams.append('query', params.query);
      if (params.author) searchParams.append('author', params.author);
      if (params.language) searchParams.append('language', params.language);
      
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: LifeQuoteItem[] 
      }>(`/life-quotes/search?${searchParams}`);
      
      return {
        success: response.success !== false,
        data: response.data || []
      };
    } catch (error: any) {
      console.error('❌ Erreur search life-quotes:', error);
      return { 
        success: false, 
        data: [], 
        error: error.message 
      };
    }
  },

  /**
   * Get available signatures
   */
  getSignatures: async (language: 'fr' | 'en' = 'fr'): Promise<ApiResponse<string[]>> => {
    try {
      const response = await this.makeRequest<{ 
        success: boolean; 
        data: string[] 
      }>(`/life-quotes/signatures?language=${language}`);
      
      return {
        success: response.success !== false,
        data: response.data || []
      };
    } catch (error: any) {
      console.error('❌ Erreur getSignatures life-quotes:', error);
      return { 
        success: false, 
        data: [], 
        error: error.message 
      };
    }
  }
};

  // ================================
  // ASSETS
  // ================================

  assets = {
    upload: async (file: File, meta?: AssetUploadMetadata): Promise<ApiResponse<AssetData>> => {
      const formData = new FormData();
      formData.append('file', file);
      if (meta) {
        Object.entries(meta).forEach(([k, v]) => v !== undefined && formData.append(k, String(v)));
      }
      const result = await this.uploadFile<ApiResponse<AssetData>>('/assets/upload', formData);
      return { success: result.success, data: result.data ?? null, error: result.error };
    },

    getAll: async (): Promise<ApiResponse<AssetData[]>> => {
      // Cache court pour éviter 429 et requêtes simultanées
  const { apiService } = await import('../services/apiService');
  const response = await apiService.get<AssetData[]>('/assets', true, {
        useCache: true,
        cacheTtlMs: 180_000, // 3 min
        dedupe: true
      });
  return { success: response.success, data: (response.data as any) ?? [], error: response.error };
    },

    delete: async (id: string): Promise<ApiResponse<boolean>> => {
      try {
        await this.makeRequest(`/assets/${id}`, { method: 'DELETE' });
        return { success: true, data: true };
      } catch (e: any) {
        return { success: false, data: false, error: e.message };
      }
    },
    /**
     * Recherche un asset par son nom (filename). Si `name` est une URL, on extrait le filename.
     */
    getByName: async (name: string, type?: string): Promise<ApiResponse<AssetData | null>> => {
      try {
        // Si l'argument ressemble à une URL, extraire le dernier segment
        let candidate = name;
        try {
          const asUrl = new URL(name);
          candidate = decodeURIComponent(asUrl.pathname.split('/').pop() || name);
        } catch { /* not an URL */ }

        const response = await this.assets.getAll();
        if (response.success && response.data) {
          const asset = response.data.find((asset: AssetData) => {
            if (!asset?.name) return false;
            const nameMatch = asset.name === candidate || candidate.includes(asset.name);
            const typeMatch = !type || asset.type === type;
            return nameMatch && typeMatch;
          });
          
          return {
            success: true,
            data: asset || null,
            message: asset ? 'Asset trouvé' : 'Asset non trouvé'
          };
        }
        return { success: false, data: null, error: 'Erreur lors de la recherche' };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Recherche un asset à partir d'une URL (helper explicite)
     */
    getByUrl: async (url: string, type?: string): Promise<ApiResponse<AssetData | null>> => {
      return this.assets.getByName(url, type);
    }

  };


  // ================================
  // SYSTEM
  // ================================

  system = {
  /**
   * Check API health
   */
    healthCheck: async (): Promise<boolean> => {
      try {
        await this.makeRequest('/stats/health');
        return true;
      } catch (error) {
        console.warn('⚠️ API health check failed:', error);
        return false;
      }
    },

  /**
   * Get dashboard statistics
   */
    getStats: async (): Promise<ApiResponse<BackendDashboardStats>> => {
      try {
        const response = await this.makeRequest<{ 
          success: boolean; 
          data: BackendDashboardStats 
        }>('/stats/dashboard');
        
        return {
          success: response.success !== false,
          data: response.data
        };
      } catch (error: any) {
        return { 
          success: false, 
          data: null, 
          error: error.message 
        };
      }
    }
  };

  // ================================
  // GENERIC METHODS
  // ================================

  /**
   * Generic method to fetch all items of a given type
   */
  getAll = async (entityType: 'projects' | 'sections' | 'education' | 'hobbies' | 'life-quotes'): Promise<ApiResponse<any[]>> => {
    try {
      switch (entityType) {
        case 'projects':
          return await this.projects.getAll();
        case 'sections':
          return await this.sections.getAll();
        case 'education':
          return await this.education.getAll();
        case 'hobbies':
          return await this.hobbies.getAll();
        case 'life-quotes':
          return await this.lifeQuotes.getAll();
        default:
          return { 
            success: false, 
            data: [], 
            error: `Type d'entité non supporté: ${entityType}` 
          };
      }
    } catch (error: any) {
      return { 
        success: false, 
        data: [], 
        error: error.message 
      };
    }
  };

  // ================================
  // AUTH MANAGEMENT
  // ================================

  /**
   * Check authentication (synchronous - cache-based)
   */
  get isAuthenticated(): boolean {
  // Check cache if recent
    if (this._authCache && (Date.now() - this._authCache.timestamp) < this.CACHE_DURATION) {
      return this._authCache.isAuth;
    }
    
  // Check token
    const token = this.getAuthToken();
    const hasValidToken = !!token && token.length > 20 && !this.isTokenExpired();
    
  // Update cache
    if (hasValidToken && !this._authCache) {
      this._authCache = {
        isAuth: true,
        timestamp: Date.now(),
        token: token
      };
    }
    
    return hasValidToken;
  }

  /**
   * Check authentication (asynchronous - with server verification)
   */
  async isAuthenticatedAsync(): Promise<boolean> {
    try {
      const now = Date.now();
      
  // Return cache if recent
      if (this._authCache && (now - this._authCache.timestamp) < this.CACHE_DURATION) {
        return this._authCache.isAuth;
      }
      
  // Check local token
      const token = this.getAuthToken();
      if (!token || this.isTokenExpired()) {
        this._authCache = { 
          isAuth: false, 
          timestamp: now, 
          token: null 
        };
        return false;
      }
      
  // Verify with server
      const result = await this.auth.verifyToken();
      const isValid = result.success && result.data?.valid === true;
      
  // Update cache
      this._authCache = { 
        isAuth: isValid, 
        timestamp: now, 
        token: isValid ? token : null 
      };
      
  // Clean up if invalid
      if (!isValid) {
        this.clearAuth();
      }
      
      return isValid;
      
    } catch (error: any) {
      console.warn('⚠️ Erreur vérification auth:', error.message);
      
      this._authCache = { 
        isAuth: false, 
        timestamp: Date.now(), 
        token: null 
      };
      
  // Only clean if it's not a network error
      if (!error.message?.includes('Network') && !error.message?.includes('fetch')) {
        this.clearAuth();
      }
      
      return false;
    }
  }

  /**
   * Refresh authentication status
   */
  async refreshAuthStatus(): Promise<boolean> {
    this._authCache = null;
    return await this.isAuthenticatedAsync();
  }

  /**
   * Get current user (from storage)
   */
  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.USER_STORAGE_KEY);
      if (!userStr) return null;
      
      const user = JSON.parse(userStr);
      return user && typeof user === 'object' ? user : null;
    } catch (error) {
  console.error('❌ Error parsing user:', error);
      return null;
    }
  }

  /**
   * Check if user is admin
   */
  get isAdmin(): boolean {
    const user = this.getCurrentUser();
    return !!(user?.is_admin || user?.isAdmin);
  }

  /**
   * Clear authentication
   */
  clearAuth(): void {
    try {
  // Clear localStorage
      localStorage.removeItem(this.TOKEN_STORAGE_KEY);
      localStorage.removeItem('portfolio_token');
      localStorage.removeItem(this.USER_STORAGE_KEY);
      
  // Clear sessionStorage
      sessionStorage.removeItem(this.TOKEN_STORAGE_KEY);
      sessionStorage.removeItem('portfolio_token');
      sessionStorage.removeItem(this.USER_STORAGE_KEY);
      
  // Clear caches
      this._authCache = null;
      this._userCache = null;
      this._verifyCache = null;
      
  console.log('🧹 Authentification nettoyée');
    } catch (error) {
  console.error('❌ Erreur nettoyage auth:', error);
    }
  }

  /**
   * Initialize authentication
   */
  async initializeAuth(): Promise<void> {
    try {
      if (this.isTokenExpired()) {
  console.log('🔄 Token expiré, nettoyage...');
        return;
      }
      
      await this.isAuthenticatedAsync();
  console.log('✅ Authentification initialisée');
    } catch (error) {
  console.error('❌ Erreur initialisation auth:', error);
    }
  }

  /**
   * Listen to authentication changes
   */
  onAuthChange(callback: (isAuth: boolean) => void): () => void {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === this.TOKEN_STORAGE_KEY || e.key === 'portfolio_token') {
        this._authCache = null;
        callback(this.isAuthenticated);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }

  /**
   * Get debug information
   */
  getDebugInfo(): any {
    return {
      isAuthenticated: this.isAuthenticated,
      isAdmin: this.isAdmin,
      user: this.getCurrentUser(),
      token: this.getAuthToken() ? 'présent' : 'absent',
      tokenExpired: this.isTokenExpired(),
      cache: this._authCache,
      timestamp: new Date().toISOString(),
      apiBaseUrl: API_BASE_URL
    };
  }

  // ================================
  // CHATBOT RAG (FastAPI /api/v1)
  // ================================

  chatbot = {
    /**
     * Dérive l'URL de base du chatbot FastAPI depuis VITE_API_URL.
     * Ex: https://jfc-webcraft.fr/api → https://jfc-webcraft.fr/api/v1/chatbot
     */
    _baseUrl: (): string => {
      if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL.replace(/\/api$/, '') + '/api/v1/chatbot';
      }
      return '/api/v1/chatbot';
    },

    /**
     * Force la réingestion complète de la base de connaissances RAG.
     * Nécessite un jeton admin valide.
     */
    reingest: async (): Promise<{ success: boolean; documents_indexed: number; message: string }> => {
      const token = portfolioApi.getAuthToken();
      const res = await fetch(`${portfolioApi.chatbot._baseUrl()}/admin/reingest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? `Erreur ${res.status}`);
      }
      return res.json();
    },
  };
}



// ================================
// SINGLETON INSTANCE
// ================================

export const portfolioApi = new PortfolioApiService();
export default portfolioApi;

// ================================
// TYPE EXPORTS
// ================================

export type {
  BackendAuthResponse,
  BackendDashboardStats,
  BackendProjectsFilter,
  BackendImageMetadata,
  BackendAboutInfo 
};

// ================================
// INITIALIZATION
// ================================

// API health test on startup
portfolioApi.system.healthCheck()
  .then((isHealthy: boolean) => {
    console.log(`🌐 Backend API ${isHealthy ? '✅ opérationnel' : '❌ hors ligne'}`);
  })
  .catch((error: any) => {
    console.warn('⚠️ Impossible de tester la santé du backend:', error.message);
  });

// Authentication initialization
portfolioApi.initializeAuth()
  .catch((error: any) => {
    console.warn('⚠️ Erreur initialisation auth:', error.message);
  });



